// SQP-332 slice B: pure helpers for the quoting-hours control. The server owns validation and the window
// label; this file only holds the zone list for the picker and the wording for the "last change" line.

export const ZONES = [
  { id: 'America/New_York', name: 'Eastern' },
  { id: 'America/Chicago', name: 'Central' },
  { id: 'America/Denver', name: 'Mountain' },
  { id: 'America/Phoenix', name: 'Arizona' },
  { id: 'America/Los_Angeles', name: 'Pacific' },
  { id: 'Pacific/Honolulu', name: 'Hawaii' },
  { id: 'America/Anchorage', name: 'Alaska' },
];

export const zoneName = tz => (ZONES.find(z => z.id === tz) || {}).name || tz;

const EN = '\u2013';

/** "06:00-20:00 Central" for a {tz,start,end}, "24/7" for null, "invalid" for anything else. */
export function shortWindow(v) {
  if (v === null || v === undefined) return '24/7';
  if (typeof v === 'object' && v.tz && /^\d\d:\d\d$/.test(v.start || '') && /^\d\d:\d\d$/.test(v.end || '')) return `${v.start}${EN}${v.end} ${zoneName(v.tz)}`;
  return 'invalid'; // never "[object Object]" or the raw garbage
}

/** "alice" from "dashboard:alice"; other actors are shown as stored. */
export const actorName = a => String(a == null ? 'unknown' : a).replace(/^dashboard:/, '');

export const DEFAULT_DRAFT = { tz: 'America/Chicago', start: '06:00', end: '20:00' };

export const draftFrom = setting => (setting ? { tz: setting.tz, start: setting.start, end: setting.end } : { ...DEFAULT_DRAFT });

export const sameDraft = (a, b) => !!a && !!b && a.tz === b.tz && a.start === b.start && a.end === b.end;

/** Client-side hint only (the server is the authority): start must be before end, both HH:MM. */
export const draftProblem = d => {
  if (!d || !/^\d\d:\d\d$/.test(d.start || '') || !/^\d\d:\d\d$/.test(d.end || '')) return 'Enter both times.';
  if (d.start >= d.end) return 'Start must be before end. A window across midnight is not supported.';
  return null;
};

/** Select options: the seven US zones, plus the draft zone itself when it is not one of them (shown by its IANA name). */
export const zoneOptions = tz => (!tz || ZONES.some(z => z.id === tz) ? ZONES : [...ZONES, { id: tz, name: tz, offList: true }]);
export const zoneOptionLabel = z => (z.offList ? z.id : `${z.name} (${z.id})`);

/** The body Save sends: exactly the edited draft. */
export const saveBody = d => ({ tz: d.tz, start: d.start, end: d.end });

/**
 * Whether Save is enabled. Never while loading or after a load error (nobody saves over a value they never saw),
 * never with a problem draft. With a stored window it also needs a change; with NO setting or an INVALID one the
 * prefilled draft can be saved as shown.
 */
export function canSave({ loaded, hasSetting, draft, base, busy }) {
  if (!loaded || busy) return false;
  if (draftProblem(draft)) return false;
  if (hasSetting && sameDraft(draft, base)) return false;
  return true;
}

/** Per-operator draft state: a draft made on one operator is never visible on another. */
export const freshState = operatorId => ({ op: operatorId, edit: null, confirm: false });
export const stateFor = (st, operatorId) => (st && st.op === operatorId ? st : freshState(operatorId));
